import React, { useState, useEffect } from 'react';
import { AppProvider } from './context/AppContext';
import HomePage from './HomePage';
import LoginPage from './components/Auth/LoginPage';
import RegisterPage from './components/Auth/RegisterPage';
import Dashboard from './Dashboard';
import ClientsPage from './components/Clients/ClientsPage';
import ProjectsPage from './components/Projects/ProjectsPage';
import TasksPage from './components/Tasks/TasksPage';
import TeamsPage from './components/Team/TeamsPage';
import NotificationsPage from './components/Dashboard/NotificationsPage';
import RevisionRequestsPage from './components/Dashboard/RevisionRequestsPage';
import SettingsPage from './components/Settings/SettingsPage';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link
} from 'react-router-dom';

const App = () => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [userType, setUserType] = useState(() => localStorage.getItem('userType'));
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [revisionRequests, setRevisionRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  // Replace with PHP API login logic
  const handleLogin = async (email, password) => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      if (data.success) {
        setUser(data.user);
        setUserType(data.user.role);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('userType', data.user.role);
      } else {
        setUser(null);
        setUserType(null);
        localStorage.removeItem('user');
        localStorage.removeItem('userType');
      }
    } catch (error) {
      console.error('Login error:', error);
      setUser(null);
      setUserType(null);
      localStorage.removeItem('user');
      localStorage.removeItem('userType');
    } finally {
      setLoading(false);
    }
  };

  // Replace with PHP API registration logic
  const handleRegister = async (name, email, password) => {
    setLoading(true);
    try {
      const response = await fetch('/api/add-user.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password })
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      if (data.success) {
        setShowRegister(false);
        // Optionally show a success message or redirect to login
      } else {
        console.error('Registration failed:', data.message);
      }
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Role-based data fetching functions
  const fetchUsers = async () => {
    if (!user) return;
    try {
      const response = await fetch('/api/get-users.php');
      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchProjects = async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams({
        userRole: userType,
        userId: user.id
      });
      const response = await fetch(`/api/get-projects.php?${params}`);
      const data = await response.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    }
  };

  const fetchTasks = async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams({
        userRole: userType,
        userId: user.id
      });
      const response = await fetch(`/api/get-tasks.php?${params}`);
      const data = await response.json();
      setTasks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
    }
  };

  const fetchClients = async () => {
    if (!user || userType !== 'admin') return;
    try {
      const response = await fetch('/api/get-clients.php');
      const data = await response.json();
      setClients(data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  };

  const fetchNotifications = async () => {
    if (!user) return;
    try {
      const response = await fetch('/api/get-notifications.php');
      const data = await response.json();
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    }
  };

  const fetchRevisionRequests = async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams({
        userRole: userType,
        userId: user.id
      });
      const response = await fetch(`/api/get-revision-requests.php?${params}`);
      const data = await response.json();
      setRevisionRequests(data.revisionRequests || []);
    } catch (error) {
      console.error('Error fetching revision requests:', error);
      setRevisionRequests([]);
    }
  };

  // Fetch all data after login
  useEffect(() => {
    if (!user) {
      setUsers([]);
      setProjects([]);
      setTasks([]);
      setClients([]);
      setNotifications([]);
      setRevisionRequests([]);
      return;
    }

    setLoading(true);
    const fetchAllData = async () => {
      await Promise.all([
        fetchUsers(),
        fetchProjects(),
        fetchTasks(),
        fetchClients(),
        fetchNotifications(),
        fetchRevisionRequests()
      ]);
      setLoading(false);
    };

    fetchAllData();
  }, [user, userType]);

  // Add a logout handler that clears localStorage
  const handleLogout = () => {
    setUser(null);
    setUserType(null);
    setUsers([]);
    setProjects([]);
    setTasks([]);
    setClients([]);
    setNotifications([]);
    setRevisionRequests([]);
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
  };

  // Function to mark notification as read
  const markNotificationAsRead = async (notificationId) => {
    try {
      await fetch('/api/mark-notification-read.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationId })
      });
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Refresh functions for components
  const refreshData = async () => {
    if (!user) return;
    setLoading(true);
    await Promise.all([
      fetchUsers(),
      fetchProjects(),
      fetchTasks(),
      fetchClients(),
      fetchNotifications(),
      fetchRevisionRequests()
    ]);
    setLoading(false);
  };

  // Minimal navigation logic
  const handleNavigateToLogin = () => {
    setShowLogin(true);
    setShowRegister(false);
  };
  const handleNavigateToRegister = () => {
    setShowRegister(true);
    setShowLogin(false);
  };
  const handleNavigateHome = () => {
    setShowLogin(false);
    setShowRegister(false);
  };

  return (
    <AppProvider value={{ 
      user, setUser, userType, setUserType, users, setUsers, projects, setProjects, tasks, setTasks, clients, setClients, notifications, setNotifications, revisionRequests, setRevisionRequests, loading, setLoading, showRegister, setShowRegister, handleLogin, handleRegister, handleLogout, markNotificationAsRead, refreshData
    }}>
      <Router>
        {user && (
          <nav style={{ position: 'fixed', top: 0, left: 0, width: '100%', background: 'var(--color-bg)', borderBottom: '1px solid var(--border-color)', padding: '0.5rem 1rem', display: 'flex', justifyContent: 'flex-start', gap: '1rem', zIndex: 100 }}>
            <Link to="/dashboard">Dashboard</Link>
            <Link to="/clients">Clients</Link>
            <Link to="/projects">Projects</Link>
            <Link to="/tasks">Tasks</Link>
            <Link to="/teams">Teams</Link>
            <Link to="/notifications">Notifications</Link>
            <Link to="/revision-requests">Revision Requests</Link>
            <Link to="/settings">Settings</Link>
            <button style={{ marginLeft: 'auto' }} onClick={handleLogout}>Logout</button>
          </nav>
        )}
        <div style={{ paddingTop: user ? '3rem' : 0 }}>
          <Routes>
            {!user ? (
              <>
                <Route path="/register" element={<RegisterPage onRegisterSuccess={handleNavigateToLogin} onNavigateToLogin={handleNavigateToLogin} />} />
                <Route path="/login" element={<LoginPage onLoginSuccess={handleLogin} onNavigateToRegister={handleNavigateToRegister} />} />
                <Route path="/" element={<HomePage onNavigateToLogin={handleNavigateToLogin} />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : (
              <>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/clients" element={<ClientsPage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/teams" element={<TeamsPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/revision-requests" element={<RevisionRequestsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </>
            )}
          </Routes>
        </div>
      </Router>
    </AppProvider>
  );
};

export default App;